import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import API_BASE_URL from '../config';
import './ComponentPlanning.css';

const ComponentPlanning = () => {
  const [components, setComponents] = useState([]);
  const [selectedComponent, setSelectedComponent] = useState('');
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenario, setSelectedScenario] = useState('Momentum Forecast');
  const [forecastData, setForecastData] = useState({
    orderQuantities: [],
    currentInventory: [],
    arrivals: [],
    componentUsage: [],
    predictedInventory: []
  });
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tooltip, setTooltip] = useState({ show: false, text: '', x: 0, y: 0 });
  const tableContainerRef = useRef(null);

  // Calculate current week and target scroll position
  const getCurrentWeek = () => {
    const now = new Date();
    const year = now.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const days = Math.floor((now - startOfYear) / (24 * 60 * 60 * 1000));
    const week = Math.floor(days / 7) + 1;
    return `${year}-${week.toString().padStart(2, '0')}`;
  };

  const getTargetWeek = () => {
    const currentWeek = getCurrentWeek();
    const [year, week] = currentWeek.split('-');
    const targetWeekNum = Math.max(1, parseInt(week) - 6);
    return `${year}-${targetWeekNum.toString().padStart(2, '0')}`;
  };

  const isFutureWeek = (weekStr) => {
    const currentWeek = getCurrentWeek();
    return weekStr > currentWeek;
  };

  // Auto-scroll to target week
  const scrollToTargetWeek = useCallback((weekList) => {
    if (!tableContainerRef.current || weekList.length === 0) return;
    
    const targetWeek = getTargetWeek();
    const targetIndex = weekList.findIndex(week => week >= targetWeek);
    
    if (targetIndex !== -1) {
      const table = tableContainerRef.current.querySelector('table');
      if (table) {
        const headerCells = table.querySelectorAll('thead th');
        if (headerCells[targetIndex + 1]) { // +1 for the "Metric" column
          const cellWidth = headerCells[targetIndex + 1].offsetWidth;
          const scrollPosition = (targetIndex + 1) * cellWidth - 200; // 200px offset
          tableContainerRef.current.scrollLeft = Math.max(0, scrollPosition);
        }
      }
    }
  }, []);

  // Fetch components from components table - only inventoried components
  useEffect(() => {
    const fetchComponents = async () => {
      try {
        console.log('Fetching components from:', `${API_BASE_URL}/api/components`);
        const response = await axios.get(`${API_BASE_URL}/api/components`);
        console.log('Components response:', response.data);
        
        // Filter to only include inventoried components
        const inventoriedComponents = response.data.filter(component => component.is_inventoried === true);
        console.log('Filtered to inventoried components:', inventoriedComponents.length);
        
        setComponents(inventoriedComponents);
        
        // Set default component to the first inventoried component if available
        if (inventoriedComponents.length > 0) {
          setSelectedComponent(inventoriedComponents[0].component_id);
        }
        
        setError('');
      } catch (error) {
        console.error('Error fetching components:', error);
        setError(`Failed to load components: ${error.message}`);
      }
    };
    fetchComponents();
  }, []);

  // Fetch scenarios from component planning API
  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        console.log('Fetching scenarios from:', `${API_BASE_URL}/api/component-planning/scenarios`);
        const response = await axios.get(`${API_BASE_URL}/api/component-planning/scenarios`);
        console.log('Scenarios response:', response.data);
        setScenarios(response.data);
        
        // Set default scenario to "Momentum Forecast" if available, otherwise first scenario
        if (response.data.includes('Momentum Forecast')) {
          setSelectedScenario('Momentum Forecast');
        } else if (response.data.length > 0) {
          setSelectedScenario(response.data[0]);
        }
        
        setError('');
      } catch (error) {
        console.error('Error fetching scenarios:', error);
        setError(`Failed to load scenarios: ${error.message}`);
      }
    };
    fetchScenarios();
  }, []);

  const fetchForecastData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      console.log('Fetching component planning data for:', selectedComponent, 'scenario:', selectedScenario);
      const response = await axios.get(`${API_BASE_URL}/api/component-planning/${selectedComponent}`, {
        params: { scenario: selectedScenario }
      });
      const data = response.data;
      console.log('Component planning data response:', data);
      
      setForecastData({
        orderQuantities: data.orderQuantities || [],
        currentInventory: data.currentInventory || [],
        arrivals: data.arrivals || [],
        componentUsage: data.componentUsage || [],
        predictedInventory: data.predictedInventory || []
      });
      
      // Extract unique weeks for table headers
      const allWeeks = new Set();
      [...data.orderQuantities, ...data.currentInventory, ...data.arrivals, ...data.componentUsage, ...data.predictedInventory]
        .forEach(item => allWeeks.add(item.week));
      const sortedWeeks = Array.from(allWeeks).sort();
      // Limit to 52 weeks to prevent overwhelming the display
      const limitedWeeks = sortedWeeks.slice(0, 52);
      setWeeks(limitedWeeks);
      
      // Auto-scroll to target week after data loads
      setTimeout(() => scrollToTargetWeek(limitedWeeks), 100);
      
    } catch (error) {
      console.error('Error fetching component planning data:', error);
      setError(`Failed to load component planning data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedComponent, selectedScenario, scrollToTargetWeek]);

  // Fetch forecast data when component or scenario changes
  useEffect(() => {
    if (selectedComponent && selectedScenario) {
      fetchForecastData();
    }
  }, [selectedComponent, selectedScenario, fetchForecastData]);

  const getValueForWeek = (dataArray, week, isSalesHistory = false) => {
    const item = dataArray.find(d => d.week === week);
    
    if (isSalesHistory && isFutureWeek(week)) {
      return null; // Return null for future sales history
    }
    
    if (item) {
      const value = Math.round(item.value);
      return value > 0 ? value : null; // Return null for 0 values to prevent graphing
    }
    
    return null;
  };

  const getDisplayValue = (value) => {
    return value !== null ? value : ''; // Show blank instead of "NULL" in table
  };

  const formatWeek = (weekStr) => {
    // Convert YYYY-WW format to more readable format
    const [year, week] = weekStr.split('-');
    return `W${week} ${year}`;
  };

  const getSelectedComponentName = () => {
    const component = components.find(c => c.component_id === selectedComponent);
    return component ? component.component_description : '';
  };

  // Helper function to truncate component names to 100 characters
  const truncateComponentName = (name) => {
    if (!name) return '';
    return name.length > 100 ? name.substring(0, 100) + '...' : name;
  };

  // Tooltip handlers
  const handleMouseEnter = (event, text) => {
    const rect = event.target.getBoundingClientRect();
    setTooltip({
      show: true,
      text,
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
  };

  const handleMouseLeave = () => {
    setTooltip({ show: false, text: '', x: 0, y: 0 });
  };

  return (
    <div className="component-planning-container">
      <div className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Component Planning Manager</h1>
          </div>
          <div className="header-tips">
            <ul>
              <li>Select inventoried component to view planning data</li>
              <li>Track order quantities and inventory levels</li>
              <li>Monitor component usage and arrivals</li>
              
            </ul>
          </div>
        </div>
        <div className="header-separator"></div>
      </div>

      <div className="page-content">
        {/* Custom Tooltip */}
        {tooltip.show && (
          <div 
            className="component-tooltip"
            style={{
              left: tooltip.x,
              top: tooltip.y,
              transform: 'translateX(-50%)'
            }}
          >
            {tooltip.text}
          </div>
        )}

        {/* Persistent Component Selector Pane */}
        <div className="product-selector-pane">
          <div className="product-selector">
            <label htmlFor="component-select">Select Inventoried Component:</label>
            <select
              id="component-select"
              value={selectedComponent}
              onChange={(e) => setSelectedComponent(e.target.value)}
              disabled={loading}
            >
              <option value="">Choose an inventoried component...</option>
              {components.map((component) => {
                const displayName = component.component_description || component.component_id;
                const truncatedName = truncateComponentName(displayName);
                const fullName = displayName;
                
                return (
                  <option 
                    key={component.component_id} 
                    value={component.component_id}
                    title={fullName.length > 100 ? fullName : ''} // Show full name as tooltip if truncated
                  >
                    {truncatedName}
                  </option>
                );
              })}
            </select>
          </div>
          
          <div className="scenario-selector">
            <label htmlFor="scenario-select">Select Scenario:</label>
            <select
              id="scenario-select"
              value={selectedScenario}
              onChange={(e) => setSelectedScenario(e.target.value)}
              disabled={loading}
            >
              {scenarios.map((scenario) => (
                <option key={scenario} value={scenario}>
                  {scenario}
                </option>
              ))}
            </select>
          </div>
        </div>

      <div className="component-planning-content">
        <div className="component-planning-header">
          <h1>Component Planning</h1>
        </div>

        {error && (
          <div className="error-message" style={{ color: 'red', padding: '10px', marginBottom: '20px' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading && (
          <div className="loading">
            <p>Loading component planning data...</p>
          </div>
        )}

        {!loading && components.length === 0 && !error && (
          <div className="loading">
            <p>Loading components...</p>
          </div>
        )}

        {selectedComponent && !loading && (
          <>
            {/* Data Table */}
            <div className="forecast-table-container" ref={tableContainerRef}>
              <table className="forecast-table">
                <thead>
                  <tr>
                    <th className="sticky-header">Metric</th>
                    {weeks.map((week) => (
                      <th key={week}>{formatWeek(week)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sticky-cell metric-label">Order Quantities</td>
                    {weeks.map((week) => {
                      const value = getValueForWeek(forecastData.orderQuantities, week);
                      return (
                        <td key={week}>
                          {getDisplayValue(value)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Current Inventory</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValue(getValueForWeek(forecastData.currentInventory, week))}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Add: Arrivals</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValue(getValueForWeek(forecastData.arrivals, week))}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Less: Usage</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValue(getValueForWeek(forecastData.componentUsage, week))}</td>
                    ))}
                  </tr>
                  <tr>
                    <td className="sticky-cell metric-label">Predicted Inventory</td>
                    {weeks.map((week) => (
                      <td key={week}>{getDisplayValue(getValueForWeek(forecastData.predictedInventory, week))}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Chart */}
            <div className="forecast-chart-container">
              <h2>Predicted Inventory Visualization</h2>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={weeks.map(week => ({
                  week: formatWeek(week),
                  'Predicted Inventory': getValueForWeek(forecastData.predictedInventory, week)
                }))}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="Predicted Inventory" 
                    stroke="#8884d8" 
                    strokeWidth={2}
                    dot={{ fill: '#8884d8' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>
      </div>
    </div>
  );
};

export default ComponentPlanning; 